import {
  CANONICAL_ORIGIN,
  CardListing,
  ImageSet,
  absoluteUrl,
  cardImage,
  lodgingBusiness,
  touristAttraction,
} from './shared'

const MAX_ITEMS = 20

export interface GridCard extends CardListing {
  __typename?: string
  description?: string | null
  location?: { lat?: number | null; lng?: number | null } | null
  featuredListingImage?: ImageSet | null
  featuredListing?: CardListing | null
  photos?: Array<{ image?: ImageSet | null } | null> | null
}

const listItemImage = (card: GridCard): string | undefined =>
  cardImage(card.promoImage) ||
  cardImage(card.featuredListingImage) ||
  cardImage(card.photos?.[0]?.image)

// The typed entity behind a card: a stay (LodgingBusiness) for listings/stories,
// a thing-to-do (TouristAttraction) for adventures.
const entity = (card: GridCard, origin: string) => {
  switch (card.__typename) {
    case 'Listing':
      return lodgingBusiness(card, origin)
    case 'Story':
      return lodgingBusiness(card.featuredListing, origin)
    case 'StoryAdventure':
    case 'ListingAdventure':
      return touristAttraction(
        {
          url: card.url,
          name: card.title,
          image: listItemImage(card),
          description: card.description,
          location: card.location,
        },
        origin,
      )
    default:
      return undefined
  }
}

const buildItemList = (
  cards: ReadonlyArray<GridCard | null | undefined> | null | undefined,
  origin: string,
) => {
  const items = (cards || [])
    .map(
      (card) =>
        card && {
          card,
          url: absoluteUrl(card.url, origin),
          image: listItemImage(card),
        },
    )
    .filter((entry): entry is { card: GridCard; url: string; image: string } =>
      Boolean(entry && entry.url && entry.image),
    )
    .slice(0, MAX_ITEMS)

  if (!items.length) return null

  return {
    '@type': 'ItemList',
    numberOfItems: items.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: items.map(({ card, url, image }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url,
      name: card.title || undefined,
      image,
      item: entity(card, origin),
    })),
  }
}

type GridCards = ReadonlyArray<GridCard | null | undefined> | null | undefined

// A standalone ItemList (e.g. the homepage), with its own @context.
export const itemListJsonLd = (
  cards: GridCards,
  { origin = CANONICAL_ORIGIN }: { origin?: string } = {},
) => {
  const list = buildItemList(cards, origin)
  return list && { '@context': 'https://schema.org', ...list }
}

const collectionPage = (
  type: string,
  cards: GridCards,
  {
    name,
    origin = CANONICAL_ORIGIN,
  }: { name?: string | null; origin?: string },
) => {
  const list = buildItemList(cards, origin)
  if (!list) return null

  return {
    '@context': 'https://schema.org',
    '@type': type,
    name: name || undefined,
    mainEntity: list,
  }
}

// A search results page wrapping the grid's ItemList.
export const searchResultsPageJsonLd = (
  cards: GridCards,
  opts: { name?: string | null; origin?: string } = {},
) => collectionPage('SearchResultsPage', cards, opts)

// A collection/region page wrapping the grid's ItemList.
export const collectionPageJsonLd = (
  cards: GridCards,
  opts: { name?: string | null; origin?: string } = {},
) => collectionPage('CollectionPage', cards, opts)
